import type { TFunction } from 'i18next';
import type { CareHistoryActor, CareHistoryEntry, CareLoadGroup } from '@/lib/disruptions/types';
import { shortOperatorLabel } from '@/lib/disruptions/operator-stamp';

// The card history in the operator's words (care-card history, 2026-10-30). The owner:
// «veo el total, pero no veo cuándo se le dio, cuánto ni por qué». Pure — every line the
// PNR's card history, the meals «Cargas» list and «Qué reciben» say comes from here, in
// the `admin` namespace (`*`). Holds KEYS and arguments, never language.

/** «4 h», «2 h 30 min», «45 min» — a delay as a person says it. */
export function durationLabel(minutes: number, t: TFunction<'admin', 'card.history'>): string {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return t('duration.minutes', { m });
  return m === 0 ? t('duration.hours', { h }) : t('duration.hoursMinutes', { h, m });
}

/** What happened — «Comida 2 de la demora», «Recarga manual», «Gasto en Café SCL»… */
export function entryTitle(e: CareHistoryEntry, t: TFunction<'admin', 'card.history'>): string {
  const n = e.rule?.tierIndex;
  switch (e.kind) {
    case 'INITIAL_LOAD':
      if (e.origin === 'MEAL_TIER' && n) return t('title.initialTier', { n });
      if (e.origin === 'CASE_ALLOWANCE') return t('title.initialAllowance');
      return t('title.initial');
    case 'TOP_UP':
      if (e.origin === 'MEAL_TIER') return t('title.tier', { n: n ?? '' });
      if (e.origin === 'CASE_ALLOWANCE') return t('title.allowance');
      if (e.origin === 'OPERATOR') return t('title.operator');
      return t('title.issuerCredit');
    case 'SCHEDULED_LOAD':
      return t('title.scheduled');
    case 'DEBIT_ADJUSTMENT':
      return t('title.debit');
    case 'SPEND':
      return e.merchant ? t('title.spendAt', { merchant: e.merchant }) : t('title.spend');
    case 'REFUND':
      return e.merchant ? t('title.refundFrom', { merchant: e.merchant }) : t('title.refund');
    case 'REDEMPTION':
      return t(e.origin === 'ISSUER' ? 'title.redeemedIssuer' : 'title.redeemed');
    case 'VOID':
      return t('title.void');
  }
}

/** Who — «Nexa, automáticamente», the operator's name, «El emisor», or «No quedó registrado quién». */
export function actorLabel(actor: CareHistoryActor, t: TFunction<'admin', 'card.history'>): string {
  switch (actor.kind) {
    case 'NEXA':
      return t('who.nexa');
    case 'ISSUER':
      return t('who.issuer');
    case 'OPERATOR':
      if (actor.name) return actor.name;
      if (actor.urn) return shortOperatorLabel(actor.urn);
      return t('who.operatorUnknown');
    default:
      return t('who.unknown');
  }
}

/** The reason code as the operator chose it. Unknown codes fall back to the code itself. */
export function reasonLabel(code: string | undefined, t: TFunction<'admin', 'card.history'>): string | undefined {
  if (!code) return undefined;
  return t(`reason.${code}`, { defaultValue: code });
}

/**
 * Why — the policy's rule for a tier («La demora llegó a 4 h; la comida 1 corresponde desde
 * 2 h · 25,00 US$ × 4 pasajeros · política «SKY — Santiago»»), the operator's reason, a
 * failure. `money` formats MINOR units in the card's currency; `null` hides amounts (a
 * viewer without `costs.view` on the operating surfaces).
 */
export function entryWhy(
  e: CareHistoryEntry,
  t: TFunction<'admin', 'card.history'>,
  money: ((minor: number) => string) | null,
  clock: (iso: string) => string = (iso) => iso,
): string[] {
  const lines: string[] = [];
  const r = e.rule;
  if (e.origin === 'MEAL_TIER' && r) {
    lines.push(
      t('why.tier', {
        delay: durationLabel(r.delayMinutes, t),
        n: r.tierIndex,
        threshold: durationLabel(r.thresholdMinutes, t),
      }),
    );
    const issued = issuedLine(r, t);
    if (issued) lines.push(issued);
  } else if (e.origin === 'CASE_ALLOWANCE') {
    lines.push(t(e.kind === 'INITIAL_LOAD' ? 'why.allowanceAtIssue' : 'why.allowanceReleased'));
  } else if (e.origin === 'OPERATOR' && (e.kind === 'TOP_UP' || e.kind === 'INITIAL_LOAD')) {
    const code = reasonLabel(e.reasonCode, t);
    const parts = [code, e.note && e.note !== code ? `«${e.note}»` : undefined].filter(Boolean);
    if (parts.length > 0) lines.push(parts.join(' — '));
  } else if (e.note) {
    lines.push(`«${e.note}»`);
  }
  if (r && money) {
    lines.push(
      [
        t('why.perPax', { amount: money(r.perPaxMinor), count: r.pax }),
        r.policyName ? t('why.policy', { name: r.policyName }) : '',
      ]
        .filter(Boolean)
        .join(' · '),
    );
  } else if (r?.policyName) {
    lines.push(t('why.policy', { name: r.policyName }));
  }
  if (e.status === 'SKIPPED') {
    lines.push(e.alreadyLoadedAt ? t('status.skipped', { time: clock(e.alreadyLoadedAt) }) : t('status.skippedPlain'));
  }
  if (e.duplicateOfAt) lines.push(t('status.duplicate', { time: clock(e.duplicateOfAt) }));
  if (e.status === 'PENDING') lines.push(t('status.pending'));
  if (e.status === 'FAILED') {
    lines.push(
      t('status.failed', {
        why: t(`failure.${e.failureCode ?? 'UNKNOWN'}`, {
          defaultValue: t('failure.UNKNOWN'),
        }),
      }),
    );
  }
  return lines;
}

/**
 * How the tier was issued (meal timing, 2026-10-30): «Forzada por Sara: familias con niños»,
 * «Por la espera real», «A la hora programada», «Al salir el vuelo», «Al declarar la demora».
 * `undefined` on an older load that says nothing.
 */
export function issuedLine(
  r: NonNullable<CareHistoryEntry['rule']>,
  t: TFunction<'admin', 'card.history'>,
): string | undefined {
  if (r.issuedOn === 'FORCED' || r.forced) {
    const by = r.forced?.by;
    const name = by?.name ?? (by?.urn ? shortOperatorLabel(by.urn) : t('who.operatorUnknown'));
    return r.forced?.reason ? t('why.issued.forcedReason', { name, reason: r.forced.reason }) : t('why.issued.forced', { name });
  }
  return r.issuedOn ? t(`why.issued.${r.issuedOn}`) : undefined;
}

/** «+60,00 US$» / «−12,50 US$»; `undefined` when the entry carries no amount. */
export function signedAmount(e: CareHistoryEntry, money: (minor: number) => string): string | undefined {
  if (typeof e.amountMinor !== 'number') return undefined;
  const sign = e.direction === 'DEBIT' ? '−' : e.direction === 'CREDIT' ? '+' : '';
  return `${sign}${money(e.amountMinor)}`;
}

/** The newest operator grant at or after `since` (ISO) — what a «Dar más comida» run produced. */
export function grantSince(groups: readonly CareLoadGroup[] | undefined, since: string): CareLoadGroup | undefined {
  const floor = Date.parse(since) - 60_000;
  return (groups ?? []).find((g) => g.origin === 'OPERATOR' && Date.parse(g.lastAt) >= floor);
}

/** The operator grants of the disruption, newest first (the «Qué reciben» extra line). */
export function operatorGrants(groups: readonly CareLoadGroup[] | undefined): CareLoadGroup[] {
  return (groups ?? []).filter((g) => g.origin === 'OPERATOR' && g.cards > g.failed);
}

/**
 * The policy's base allowance on the disruption's cards (`CASE_ALLOWANCE`) — money that is
 * NOT a meal tier, so «Qué reciben» names it apart (H2270, 2026-11-03: «Comida: todavía no
 * corresponde» beside «79 vales enviados» — the cards carried the allowance, not a meal).
 * `perPaxMinor` only when every batch gave the same per passenger. Null when none.
 */
export function caseAllowance(
  groups: readonly CareLoadGroup[] | undefined,
): { cards: number; currency: string; perPaxMinor?: number } | null {
  const rows = (groups ?? []).filter((g) => g.origin === 'CASE_ALLOWANCE' && g.cards > g.failed);
  if (rows.length === 0) return null;
  const per = new Set(rows.map((g) => g.perPaxMinor));
  const perPaxMinor = per.size === 1 ? rows[0]!.perPaxMinor : undefined;
  return {
    cards: rows.reduce((n, g) => n + g.cards - g.failed, 0),
    currency: rows[0]!.currency,
    ...(perPaxMinor !== undefined ? { perPaxMinor } : {}),
  };
}
